import { CaretDownOutlined, CaretRightOutlined, LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useMount } from 'ahooks';
import { Checkbox, Tooltip, message } from 'antd';
import useSmartField from 'hooks/useSmartField';
import { useEffect, useState, HTMLAttributes, memo } from 'react';
import { useTranslate } from '@tolgee/react';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import Fold from 'views/Conversation/Sider/Fold';
import { FV, PlatformConfig, Store } from './share';
import useCheck from './useCheck';
import { useConfigList } from './useConfigList';
import { useGetRegionLabel } from 'hooks/business/useRegion';

export interface PlatformRegionListProps extends HTMLAttributes<HTMLDivElement> {
  departmentId?: number;
  permId?: General.PermissionId;
  platform?: General.PlatformUpperCaseTunnel;
  supportWa?: boolean;
  storeDataSource?: Store[];
  singleRegion?: boolean | string;
  singlePlatform?: boolean | string;
}

function PlatformRegionList(props: PlatformRegionListProps) {
  const {
    departmentId,
    permId,
    platform,
    supportWa = true,
    storeDataSource,
    singleRegion,
    singlePlatform,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const { loading, configList, fetch } = useConfigList({
    supportWa,
    departmentId,
    platform,
    storeDataSource,
    permId,
  });

  useMount(() => {
    fetch().catch(() => {
      message.error(t('marketing.获取店铺列表失败，请稍后再试'));
    });
  });

  const {
    field: { value: active, onChange: onActiveChange },
  } = useSmartField<FV['active']>('active');

  const {
    field: { value: storeScopes },
  } = useSmartField<FV['storeScopes']>('storeScopes');

  const [
    {
      platform: firstSelectedPlatform = undefined,
      regionStoreScopes: [{ region: firstSelectedRegion = undefined } = {}] = [],
    } = {},
  ] = storeScopes;

  useEffect(() => {
    if (configList.length > 0 && !active) {
      if (singleRegion && firstSelectedPlatform && firstSelectedRegion) {
        onActiveChange({
          platform: firstSelectedPlatform,
          region: firstSelectedRegion,
        });

        return;
      }

      if (singlePlatform && firstSelectedPlatform) {
        onActiveChange({
          platform: firstSelectedPlatform,
          region: 'all',
        });

        return;
      }

      const [{ platform, regionList: [{ region = undefined } = {}] = [] }] = configList;

      onActiveChange({
        platform,
        region: singleRegion && region ? region : 'all',
      });
    }
  }, [
    configList,
    active,
    singleRegion,
    singlePlatform,
    firstSelectedPlatform,
    firstSelectedRegion,
    onActiveChange,
  ]);

  const list = configList.map((config, index) => {
    return (
      <PlatformRegion
        key={config.platform}
        config={config}
        defaultFold={
          singleRegion && firstSelectedPlatform
            ? config.platform !== firstSelectedPlatform
            : index !== 0
        }
        singleRegion={singleRegion}
        singlePlatform={singlePlatform}
      />
    );
  });

  return <div {...otherProps}>{loading ? <LoadingOutlined /> : list}</div>;
}

interface PlatformRegionProps {
  config: PlatformConfig;
  defaultFold?: boolean;
  singleRegion?: boolean | string;
  singlePlatform?: boolean | string;
}

function PlatformRegion(props: PlatformRegionProps) {
  const { config, defaultFold = true, singleRegion, singlePlatform } = props;

  const { platform, regionList, count } = config;

  const [isfold, setIsfold] = useState(defaultFold);

  const {
    field: { value: active, onChange: onActiveChange },
  } = useSmartField<FV['active']>('active');

  const getRegionLabel = useGetRegionLabel();

  const { platform: activePlatform, region: activeRegion } = active || {};

  const platformActive = activePlatform === platform;

  const {
    value,
    isPlatformChecked,
    isPlatformIndeterminate,
    isRegionChecked,
    isRegionIndetermintate,
    onPlatformCheck,
    onRegionCheck,
  } = useCheck();

  const [
    {
      platform: firstSelectedPlatform = undefined,
      regionStoreScopes: [{ region: firstSelectedRegion = undefined } = {}] = [],
    } = {},
  ] = value;

  const platformChecked = isPlatformChecked(config);

  const platformIndeterminate = !platformChecked && isPlatformIndeterminate(platform);

  const platformDisabled = Boolean(
    singlePlatform &&
      firstSelectedPlatform &&
      platform !== firstSelectedPlatform &&
      !platformChecked &&
      !platformIndeterminate,
  );

  const platformCheckbox = singleRegion ? null : (
    <Checkbox
      disabled={platformDisabled}
      checked={platformChecked}
      indeterminate={platformIndeterminate}
      onClick={(e) => e.stopPropagation()}
      onChange={({ target: { checked } }) => onPlatformCheck(checked, { platform, count })}
    />
  );

  const { [toLowerCase(platform)]: platformLabel } = platformLabelMap;

  if (regionList.length === 0 || platform === 'FACEBOOK') {
    return (
      <Container>
        <Platform
          active={platformActive}
          onClick={() =>
            !platformDisabled &&
            onActiveChange({
              platform,
              region: 'all',
            })
          }
        >
          <CaretRightOutlined style={{ opacity: 0 }} />
          {platformCheckbox}
          <span>{platformLabel}</span>
        </Platform>
      </Container>
    );
  }

  return (
    <Container>
      <Fold
        isfold={isfold}
        fixedCount={1}
        nodeList={[
          <Platform
            key={platform}
            active={platformActive}
            onClick={() =>
              singleRegion ||
              (singlePlatform && firstSelectedPlatform && platform !== firstSelectedPlatform)
                ? setIsfold(!isfold)
                : onActiveChange({
                    platform,
                    region: 'all',
                  })
            }
          >
            <div
              onClick={(e) => {
                e.stopPropagation();

                setIsfold(!isfold);
              }}
            >
              {isfold ? <CaretRightOutlined /> : <CaretDownOutlined />}
            </div>
            {platformCheckbox}
            {typeof singleRegion === 'string' &&
            firstSelectedPlatform &&
            firstSelectedPlatform !== platform ? (
              <Tooltip title={singleRegion} placement="bottom">
                <span>{platformLabel}</span>
              </Tooltip>
            ) : typeof singlePlatform === 'string' && platformDisabled ? (
              <Tooltip title={singlePlatform}>
                <span>{platformLabel}</span>
              </Tooltip>
            ) : (
              <span>{platformLabel}</span>
            )}
          </Platform>,
          <RegionList key="regionList">
            {regionList.map((regionConfig) => {
              const { region, count } = regionConfig;

              const checked = isRegionChecked(platform, regionConfig);

              const indeterminate = !checked && isRegionIndetermintate(platform, regionConfig);

              const disabled =
                platformDisabled ||
                (Boolean(singleRegion && firstSelectedPlatform && firstSelectedRegion) &&
                  (platform !== firstSelectedPlatform || region !== firstSelectedRegion));

              const content = (
                <Region
                  key={region}
                  active={platformActive && activeRegion === region}
                  onClick={() =>
                    !disabled &&
                    onActiveChange({
                      platform,
                      region,
                    })
                  }
                >
                  <Checkbox
                    disabled={disabled && !checked && !indeterminate}
                    checked={checked}
                    indeterminate={indeterminate}
                    onClick={(e) => e.stopPropagation()}
                    onChange={({ target: { checked } }) => {
                      onRegionCheck(checked, { platform, region, count });
                      if (checked) {
                        // 选中时切换到此站点
                        requestAnimationFrame(() => {
                          onActiveChange({
                            platform,
                            region,
                          });
                        });
                      }
                    }}
                  />
                  {<span>{getRegionLabel(region)}</span>}
                </Region>
              );

              return typeof singleRegion === 'string' && disabled ? (
                <Tooltip title={singleRegion} placement="bottom">
                  {content}
                </Tooltip>
              ) : typeof singlePlatform === 'string' && disabled ? (
                <Tooltip title={singlePlatform} placement="bottom">
                  {content}
                </Tooltip>
              ) : (
                content
              );
            })}
          </RegionList>,
        ]}
      />
    </Container>
  );
}

export default memo((props: PlatformRegionListProps) => {
  return <PlatformRegionList key={props.departmentId} {...props} />;
});

const Container = styled.div`
  border-bottom: solid 1px var(--fett-border-default);
`;

const Item = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ active }) => (active ? 'var(--ant-primary-color)' : 'var(--fett-text-secondary)')};
  background: ${({ active }) => (active ? 'var(--ant-primary-1)' : 'transparent')};
  cursor: pointer;
  > * + * {
    margin-left: 4px;
  }
`;

const Platform = styled(Item)`
  height: 40px;
  padding: 0 8px;
`;

const Region = styled(Item)`
  height: 28px;
  padding: 0 8px 0 36px;
`;

const RegionList = styled.div`
  padding: 8px 0;
`;
